import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Outlet } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuthStore, useNotificationStore } from '../../stores';
import { Footer, theme } from '@bloomays-lib/ui.shared';
import { Role } from '@bloomays-lib/types.shared';
import Sidebar from '../organisms/Sidebar';
import Breadcrumb from '../molecules/Breadcrumb';

type BucketProps = {
  showFooter: boolean;
};

const COLLAPSE_STORAGE_KEY = 'bloomays.sidebar.collapsed';

const Bucket = ({ showFooter = false }: BucketProps) => {
  const auth = useAuthStore((state) => state.auth);
  const user = auth?.user;
  const notificationCenterContext = useNotificationStore();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const roles = user?.roles || [];
  const isAnonymous = roles.includes(Role.Anonymous);
  // Le menu latéral n'a de sens que pour un utilisateur authentifié et reconnu.
  const showSidebar = Boolean(user) && !isAnonymous;

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
    }
  }, [collapsed]);

  return (
    <ContainerBucket>
      <Body>
        {showSidebar && (
          <Sidebar
            roles={roles}
            user={{ picture: user?.picture || '', displayName: user?.displayName || '' }}
            logout={auth?.logout}
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((prev) => !prev)}
            mobileOpen={mobileOpen}
            onMobileClose={() => setMobileOpen(false)}
          />
        )}
        <Main>
          {showSidebar && (
            <Breadcrumb
              onMenuClick={isMobile ? () => setMobileOpen(true) : undefined}
              notificationCenterContext={notificationCenterContext}
            />
          )}
          <Scrollable>
            <Content>
              <Outlet />
            </Content>
            {showFooter ? <Footer release={import.meta.env.VITE_RELEASE} env={import.meta.env.VITE_ENV} /> : ''}
          </Scrollable>
        </Main>
      </Body>
    </ContainerBucket>
  );
};

export default Bucket;

const ContainerBucket = styled('div')(() => ({
  margin: 0,
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const Body = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
}));

const Main = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
}));

const Scrollable = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
}));

const Content = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  paddingTop: '28px',
  [theme.breakpoints.down('md')]: {
    paddingTop: '20px',
  },
}));
